import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import { billRunsService } from '../services/billRunsService';
import { JobCard } from '../components/jobs/JobCard';
import { timeAgo } from '../lib/timeAgo';
import { UsageDataFlowSection } from '../components/jobs/UsageDataFlowSection';
import { buildBillRunRows } from '../lib/billRunRows';
import { NO_RUN_SITE_FILTERS } from '../lib/billRunConditions';
import { billRunStatus } from '../lib/billRunStatus';
import { lastCompletedMonth } from '../lib/billRunsUrl';
import { runDurationLabel } from '../lib/billRunLog';
import { runsLink, usageFlow, type FlowStep } from '../lib/dashboardMetrics';
import {
  backlogHealth, watermarkHealth, worst,
  type Health, type JobHealth, type JobKey, type JobsStatus,
} from '../lib/jobHealth';
import { SILENT_DAYS_BEFORE_ALERT } from '../components/ProviderIngestMonitor';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const REFRESH_MS = 60_000;

type Snapshot = { data: JobsStatus; backlog: Health };
type Prev = { pending: number; at: number } | null;

const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'x-api-key': 'scopingai',
    Authorization: `Bearer ${session?.access_token ?? ''}`,
    'x-user-id': session?.user?.id ?? '',
  };
};

const Stat = ({ children }: { children: React.ReactNode }) => <p className="break-words">{children}</p>;

const counts = (m: Record<string, number>): string =>
  Object.entries(m).map(([k, v]) => `${v} ${k}`).join(' · ') || 'none';

export function JobsPage() {
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [silent, setSilent] = useState<string[]>([]);
  const prevRef = useRef<Prev>(null);
  const aliveRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/bill-validation/jobs/status`, { headers: await getAuthHeaders() });
      const json = await res.json() as { success: boolean; data?: JobsStatus; error?: string };
      if (!json.success || !json.data) throw new Error(json.error || 'Failed to load job status');
      if (!aliveRef.current) return;
      const now = Date.now();
      const b = json.data.extras.billBacklog;
      const prev = prevRef.current;
      const finishedAt = b.lastRun ? (b.lastRun.finished_at ?? (b.lastRun.status === 'running' ? b.lastRun.started_at : null)) : null;
      const backlog = backlogHealth({
        pending: b.pending, prevPending: prev?.pending ?? null, lastRunFinishedAt: finishedAt,
        finishedSincePrev: !!(finishedAt && prev && Date.parse(finishedAt) > prev.at), now,
      });
      prevRef.current = { pending: b.pending, at: now };
      setSnap({ data: json.data, backlog });
      setError(null);
    } catch (err) {
      if (aliveRef.current) setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    void load();
    const tick = () => { if (document.visibilityState === 'visible') void load(); };
    const id = setInterval(tick, REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      aliveRef.current = false;
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  // The last completed month's bills: where unfinished bills wait (usageFlow gaps). Heavy, so it loads on
  // mount and on Refresh/Retry, not on the 60 s interval.
  const month = useMemo(() => lastCompletedMonth(), []);
  const [flow, setFlow] = useState<FlowStep[] | null>(null);
  const [parseFailed, setParseFailed] = useState(0);
  const [usageByProvider, setUsageByProvider] = useState<Record<string, number>>({});
  const [flowAt, setFlowAt] = useState<number | null>(null);
  const [flowError, setFlowError] = useState<string | null>(null);
  const loadFlow = useCallback(async () => {
    try {
      const res = await billRunsService.fetch({ months: [month], mode: 'end', siteFilters: NO_RUN_SITE_FILTERS, includeArchived: false });
      if (!aliveRef.current) return;
      if (!res.success || !res.data) throw new Error(res.error || 'Failed to load bills');
      const d = res.data;
      const rows = buildBillRunRows({
        bills: d.bills, emails: d.emails, queue: d.queue, analyses: d.analyses,
        sendHistory: d.sendHistory, sites: d.sites, tags: d.tags, lastBillTo: d.lastBillTo,
      }, 'end', [month], () => null);
      const byProvider: Record<string, number> = {};
      const seen = new Set<string>();
      for (const r of rows) {
        if (r.kind !== 'bill' || r.archived || seen.has(r.board.bill.id)) continue;
        seen.add(r.board.bill.id);
        if (r.board.pipeline.current !== 'usage') continue; // parsed, usage not applied: the usage gap
        const p = r.site?.bv_extn?.metering_provider || 'unknown';
        byProvider[p] = (byProvider[p] ?? 0) + 1;
      }
      setFlow(usageFlow(rows));
      setParseFailed(rows.filter((r) => r.kind === 'bill' && billRunStatus(r).details === 'parse_failed').length);
      setUsageByProvider(byProvider);
      setFlowAt(Date.now());
      setFlowError(null);
    } catch (err) {
      // the backlog card still shows the counts without the split
      console.error('Jobs: usage flow load failed', err);
      if (aliveRef.current) setFlowError(err instanceof Error ? err.message : 'Failed to load bills');
    }
  }, [month]);
  useEffect(() => { void loadFlow(); }, [loadFlow]);
  const refreshAll = () => { void load(); void loadFlow(); };

  if (loading && !snap) {
    return (
      <div className="w-full space-y-4 px-4 py-6 sm:px-6 lg:px-8">
        {[...Array(4)].map((_, i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-gray-100" />)}
      </div>
    );
  }
  if (!snap) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" /> <span className="min-w-0 break-words">{error ?? 'Failed to load'}</span>
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => { setLoading(true); refreshAll(); }}>Retry</Button>
        </div>
      </div>
    );
  }

  const { data, backlog } = snap;
  const job = (k: JobKey): JobHealth | undefined => data.jobs.find((j) => j.job === k);
  const { emailIntake: ei, parsing, unmatched, reportSends, gcs, billBacklog: bb } = data.extras;
  const now = Date.now();
  const gap = (a: number, b: number) => (flow ? Math.max(0, flow[a].count - flow[b].count) : null);
  const notParsed = gap(0, 1);
  const onUsage = gap(1, 2);
  const onRun = gap(2, 3);
  const blockedOnUsage = flow ? Math.max(0, flow[1].count - flow[2].count) : 0;
  const link = (patch: Parameters<typeof runsLink>[1] = {}) => runsLink(month, patch);
  const sweep = job('report-sweep');
  const tick = job('tick');
  const stuck = data.stuckLocks.length + data.deadWorkers.length;
  const gcsHealth = worst(job('sync-gcs')?.health ?? 'off', silent.length > 0 ? 'amber' : 'green');
  const emailHealth = job('email-sync')?.health === 'paused'
    ? 'paused' : worst(job('email-sync')?.health ?? 'off', watermarkHealth(ei.lastPolledAt, now));

  return (
    <div className="w-full space-y-4 px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Jobs</h1>
          <p className="mt-0.5 text-sm text-gray-500">Background processes: is each one alive? Job status refreshes every minute.</p>
        </div>
        <Button variant="outline" className="gap-2 self-start" onClick={refreshAll}>
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" /> <span className="min-w-0 break-words">Refresh failed: {error}</span>
          <Button variant="outline" size="sm" className="ml-auto" onClick={refreshAll}>Retry</Button>
        </div>
      )}

      {data.paused.paused && (
        <div data-testid="jobs-paused" className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-300 bg-gray-100 px-4 py-3 text-sm text-gray-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          The pipeline is paused{data.paused.since ? ` since ${new Date(data.paused.since).toLocaleString('en-AU')}` : ''}. Tick-driven jobs are not running.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <JobCard testId="job-card-tick" title="Pipeline tick" health={tick?.health ?? 'off'} jobs={[tick]}>
          {data.tickSteps.length > 0 && (
            <ul className="space-y-0.5">
              {data.tickSteps.map((s) => (
                <li key={`${s.job}-${s.started_at}`} className={cn('break-words', s.status === 'failed' && 'text-red-700')}>
                  {s.job}: {s.status}{s.status === 'failed' && s.error ? ` · ${s.error.split('\n')[0]}` : ''}
                </li>
              ))}
            </ul>
          )}
        </JobCard>

        <JobCard testId="job-card-email" title="Email intake" health={emailHealth} jobs={[job('email-sync')]}
          link={{ to: '/bill-validation/inbox', label: 'Inbox' }}>
          <Stat>Queue: {counts(ei.itemsByStatus)}</Stat>
          <Stat>Live workers: {ei.liveWorkers}</Stat>
          {ei.activeJob && <Stat>Active sync: {ei.activeJob.kind} ({ei.activeJob.state}), started {timeAgo(ei.activeJob.started_at, now)}</Stat>}
          <Stat>Watermark: last polled {timeAgo(ei.lastPolledAt, now)}</Stat>
          <Stat>Last 24 h: {ei.runs24h.automatic} automatic, {ei.runs24h.manual} manual</Stat>
        </JobCard>

        <JobCard testId="job-card-parsing" title="Invoice parsing" health={parsing.health} jobs={[job('parse-worker')]}
          link={{ to: '/bill-validation/inbox', label: 'Inbox' }}>
          <Stat>Queue: {counts(parsing.byStatus)}</Stat>
          <Stat>Oldest pending: {parsing.oldestPendingAt ? timeAgo(parsing.oldestPendingAt, now) : 'none'}</Stat>
          <Stat>Stalled: {parsing.stalled}</Stat>
          {parseFailed > 0 && (
            <Link to={link({ conditions: { details: ['parse_failed'] } })} className="text-red-700 hover:underline">
              {parseFailed} failed parse{parseFailed === 1 ? '' : 's'}
            </Link>
          )}
        </JobCard>

        <JobCard testId="job-card-rematch" title="Unmatched recovery" health={job('email-rematch')?.health ?? 'off'} jobs={[job('email-rematch')]}
          link={{ to: '/bill-validation/inbox', label: 'Inbox' }}>
          <Stat>Never re-checked: {unmatched.neverRechecked} · Re-read pending: {unmatched.rereadPending} · Re-read gave up: {unmatched.rereadGaveUp}</Stat>
        </JobCard>

        <JobCard testId="job-card-bill-run" title="Bill run" health={job('bill-run-slice')?.health ?? 'off'}
          jobs={[job('bill-run-slice'), job('bill-run-nightly')]} link={{ to: link(), label: 'Bill Runs' }} />

        <JobCard testId="job-card-backlog" title="Bill pipeline backlog" health={backlog} link={{ to: link(), label: 'Bill Runs' }}>
          <Stat>Bill run: {bb.pending} pending · {bb.running} running · {bb.error} error</Stat>
          {bb.lastRun ? (
            <Stat>
              Last bill run ({bb.lastRun.manual ? 'manual' : 'automatic'}): {new Date(bb.lastRun.started_at).toLocaleString('en-AU')} · {bb.lastRun.status}
              {bb.lastRun.finished_at && ` in ${runDurationLabel(bb.lastRun.started_at, bb.lastRun.finished_at)}`}
            </Stat>
          ) : <Stat>No bill run yet</Stat>}
          <div data-testid="job-backlog-waiting">
            <p className="font-medium text-gray-600">Where {month} bills wait{flowAt ? ` (as of ${new Date(flowAt).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })})` : ''}</p>
            {flow ? (
              <ul className="space-y-0.5">
                <li><Link className="text-blue-700 hover:underline" to={link({ conditions: { workflow: ['missing_invoice'] } })}>{notParsed} not parsed yet</Link></li>
                <li><Link className="text-blue-700 hover:underline" to={link({ conditions: { details: ['missing_usage_data'] } })}>{onUsage} waiting on usage data</Link></li>
                <li><Link className="text-blue-700 hover:underline" to={link()}>{onRun} waiting on the bill run</Link></li>
              </ul>
            ) : flowError
              ? <p className="break-words text-red-700">Could not load bills: {flowError}</p>
              : <p className="text-gray-500">Loading bills</p>}
            {flow && onUsage ? (
              <p data-testid="job-backlog-usage-providers">
                Waiting on usage: {Object.entries(usageByProvider).map(([p, n]) => `${p} ${n}`).join(' · ') || 'none'}
              </p>
            ) : null}
          </div>
        </JobCard>

        <JobCard testId="job-card-gcs" wide title="GCS usage" health={gcsHealth} jobs={[job('sync-gcs')]}
          link={{ to: '/bill-validation/sites', label: 'Sites' }}>
          {gcs.lastExecution ? (
            <Stat>Last execution: {gcs.lastExecution.tasksOk}/{gcs.lastExecution.taskCount} tasks ok, started {timeAgo(gcs.lastExecution.started_at, now)}</Stat>
          ) : <Stat>No execution recorded</Stat>}
          <Stat>Files: {gcs.files.done} done · {gcs.files.errored} errored · {gcs.files.empty} empty · {gcs.rowsInserted.toLocaleString('en-AU')} rows inserted</Stat>
          <Stat>{gcs.unmatchedNmiCount} unmatched NMI{gcs.unmatchedNmiCount === 1 ? '' : 's'}</Stat>
          {silent.length > 0 && (
            <p className="break-words text-amber-700">
              Silent {SILENT_DAYS_BEFORE_ALERT}+ days: {silent.join(', ')}
              {blockedOnUsage > 0 && ` (${blockedOnUsage} bill${blockedOnUsage === 1 ? '' : 's'} waiting on usage data)`}
            </p>
          )}
          <UsageDataFlowSection
            month={month} flow={flow} blockedOnUsage={blockedOnUsage} parseFailed={parseFailed}
            lastRun={bb.lastRun} onSilent={setSilent}
          />
        </JobCard>

        <JobCard testId="job-card-reports" title="Report sends" health={sweep?.health ?? 'off'} jobs={[sweep]}
          link={{ to: '/bill-validation/reports', label: 'Reports' }}>
          {reportSends.length === 0 ? <Stat>No active or failed batches in the last 7 days</Stat> : (
            <ul className="space-y-0.5">
              {reportSends.map((r) => (
                <li key={r.id} className="break-words">
                  {r.status} · {timeAgo(r.created_at, now)} · {r.sent} sent, {r.failed} failed
                </li>
              ))}
            </ul>
          )}
        </JobCard>

        {stuck > 0 && (
          <JobCard testId="job-card-stuck" title="Stuck locks & dead workers" health="amber">
            <ul className="space-y-0.5">
              {data.stuckLocks.map((l) => (
                <li key={l.job_name} className="break-words">Lock {l.job_name} held by {l.holder ?? 'unknown'} since {timeAgo(l.locked_at, now)}</li>
              ))}
              {data.deadWorkers.map((w) => (
                <li key={w.id} className="break-words">Worker {w.id} ({w.job_id}) last heartbeat {timeAgo(w.heartbeat_at, now)}</li>
              ))}
            </ul>
          </JobCard>
        )}
      </div>
    </div>
  );
}
